<template>
  <Teleport to="body">
    <Transition name="sdcm-overlay">
      <div class="sdcm-overlay" @click.self="stage === 'summary' ? $emit('close') : null">
        <Transition name="sdcm-modal" appear>
          <div class="sdcm-modal">
            <div class="sdcm-header">
              <span class="mdi mdi-bank-transfer sdcm-header-icon"></span>
              <span class="sdcm-header-title">Confirmar distribución y transferencia</span>
              <button v-if="stage === 'summary'" class="sdcm-close-btn" @click="$emit('close')"><span class="mdi mdi-close"></span></button>
            </div>

            <div v-if="stage === 'summary'" class="sdcm-body">
              <div v-if="loading" class="sdcm-loading">Cargando resumen...</div>

              <template v-else-if="summary">
                <div class="sdcm-metrics">
                  <div class="sdcm-metric">
                    <span class="sdcm-metric-label">Total del mes</span>
                    <span class="sdcm-metric-value sdcm-metric-value--mono">{{ formatCurrency(summary.totalAmount) }}</span>
                  </div>
                  <div class="sdcm-metric">
                    <span class="sdcm-metric-label">Inquilinos</span>
                    <span class="sdcm-metric-value">{{ summary.tenantCount }}</span>
                    <span v-if="summary.plotCount !== summary.tenantCount" class="sdcm-metric-sub">({{ summary.plotCount }} naves)</span>
                  </div>
                  <div class="sdcm-metric">
                    <span class="sdcm-metric-label">Cargo promedio</span>
                    <span class="sdcm-metric-value sdcm-metric-value--mono">{{ formatCurrency(summary.avgCharge) }}</span>
                    <span v-if="summary.trend" class="mdi sdcm-trend" :class="[summary.trend === 'up' ? 'mdi-arrow-up-bold sdcm-trend--up' : 'mdi-arrow-down-bold sdcm-trend--down']"></span>
                  </div>
                </div>

                <div class="sdcm-charges">
                  <div class="sdcm-charges-header">
                    <span class="sdcm-charges-title">Cargos del mes</span>
                    <span class="sdcm-charges-note">Los 5 mayores</span>
                  </div>
                  <div v-for="(c, i) in summary.topCharges" :key="i" class="sdcm-charge-row" :class="{ 'sdcm-charge-row--alt': i % 2 === 1 }">
                    <span v-if="c.anomaly" class="sdcm-anomaly-dot" :title="`Mes anterior: ${formatCurrency(c.prevAvg)} · ${c.diffPercent > 0 ? '+' : ''}${c.diffPercent}%`"></span>
                    <span class="sdcm-charge-tenant">{{ c.tenantName }}</span>
                    <span class="sdcm-charge-plot">{{ c.plotName }}</span>
                    <span class="sdcm-charge-amount">{{ formatCurrency(c.amount) }}</span>
                  </div>
                  <button class="sdcm-view-all" @click="$emit('view-distribution')">Ver distribución completa</button>
                </div>

                <div class="sdcm-info-note">
                  <span class="mdi mdi-information-outline sdcm-info-icon"></span>
                  <span class="sdcm-info-text">
                    Al confirmar, los cargos se transferirán a cobranza y estarán disponibles en los estados de cuenta de cada inquilino.
                    Esta acción puede revertirse reabriendo el mes, lo que eliminará los cargos transferidos.
                  </span>
                </div>

                <label class="sdcm-checkbox-row">
                  <input type="checkbox" v-model="confirmed" class="sdcm-checkbox" />
                  <span>Confirmo que los servicios del mes están completos y los montos son correctos</span>
                </label>
              </template>
            </div>

            <div v-else-if="stage === 'processing'" class="sdcm-state">
              <span class="mdi mdi-loading sdcm-spinner"></span>
              <span class="sdcm-state-text">Transfiriendo cargos a cobranza...</span>
            </div>

            <div v-else-if="stage === 'success'" class="sdcm-state">
              <span class="mdi mdi-check-circle sdcm-success-icon"></span>
              <span class="sdcm-state-title sdcm-state-title--success">Distribución completada</span>
              <span class="sdcm-state-line">{{ summary.tenantCount }} inquilinos con cargos transferidos</span>
              <span class="sdcm-state-line">{{ formatCurrency(summary.totalAmount) }} transferidos en total</span>
              <span class="sdcm-state-line">{{ periodLabel }}</span>
            </div>

            <div v-else-if="stage === 'error'" class="sdcm-state">
              <span class="mdi mdi-close-circle sdcm-error-icon"></span>
              <span class="sdcm-state-title sdcm-state-title--error">Error en la transferencia</span>
              <span class="sdcm-state-line">{{ errorMessage }}</span>
              <div class="sdcm-error-actions">
                <button class="sdcm-retry-btn" @click="onConfirmClick">Intentar de nuevo</button>
                <button class="sdcm-cancel-btn" @click="$emit('close')">Cancelar</button>
              </div>
            </div>

            <div v-if="stage === 'summary'" class="sdcm-footer">
              <button class="sdcm-footer-cancel" @click="$emit('close')">Cancelar</button>
              <button class="sdcm-footer-confirm" :disabled="!confirmed" @click="onConfirmClick">Confirmar distribución</button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { getConfirmationSummary, confirmDistribution } from '@/services/services'

const props = defineProps({
  parkId: { type: String, required: true },
  period: { type: String, required: true },
})

const emit = defineEmits(['close', 'view-distribution', 'confirmed'])

const stage = ref('summary')
const loading = ref(true)
const summary = ref(null)
const confirmed = ref(false)
const errorMessage = ref('')

const periodLabel = computed(() => {
  const [y, m] = props.period.split('-').map(Number)
  const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
  return `${MONTHS[m - 1]} ${y}`
})

onMounted(async () => {
  try {
    summary.value = await getConfirmationSummary(props.parkId, props.period)
  } finally {
    loading.value = false
  }
})

async function onConfirmClick() {
  if (!confirmed.value && stage.value === 'summary') return
  stage.value = 'processing'
  errorMessage.value = ''
  try {
    await confirmDistribution(props.parkId, props.period)
    stage.value = 'success'
    setTimeout(() => {
      emit('confirmed')
    }, 2500)
  } catch (error) {
    errorMessage.value = error.message
    stage.value = 'error'
  }
}

function formatCurrency(n) {
  if (n === null || n === undefined) return '—'
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n)
}
</script>

<style scoped>
.sdcm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.sdcm-overlay-enter-active, .sdcm-overlay-leave-active { transition: opacity 150ms ease; }
.sdcm-overlay-enter-from, .sdcm-overlay-leave-to { opacity: 0; }

.sdcm-modal-enter-active { transition: opacity 200ms ease, transform 200ms ease; }
.sdcm-modal-enter-from { opacity: 0; transform: scale(0.95); }

.sdcm-modal {
  width: 520px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.sdcm-header {
  height: 64px;
  flex-shrink: 0;
  background: #0f172a;
  border-radius: 12px 12px 0 0;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0 1.25rem;
}

.sdcm-header-icon { color: #ffffff; font-size: 24px; }
.sdcm-header-title { flex: 1; font-family: var(--font-display); font-weight: 700; font-size: 16px; color: #ffffff; }
.sdcm-close-btn { background: none; border: none; color: rgba(255, 255, 255, 0.6); cursor: pointer; display: flex; }

.sdcm-body { padding: 1rem 1.25rem; display: flex; flex-direction: column; gap: 0.75rem; }
.sdcm-loading { padding: 2rem; text-align: center; font-family: var(--font-body); font-size: 13px; color: #94a3b8; }

.sdcm-metrics { display: flex; height: 80px; }
.sdcm-metric {
  flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.2rem;
  border-right: 1px solid #f1f5f9;
}
.sdcm-metric:last-child { border-right: none; }
.sdcm-metric-label { font-family: var(--font-body); font-size: 10px; text-transform: uppercase; color: #94a3b8; }
.sdcm-metric-value { font-family: var(--font-display); font-weight: 700; font-size: 22px; color: #1e3a5f; }
.sdcm-metric-value--mono { font-family: var(--font-mono); }
.sdcm-metric-sub { font-family: var(--font-body); font-size: 10px; color: #94a3b8; }
.sdcm-trend { font-size: 12px; }
.sdcm-trend--up { color: #dc2626; }
.sdcm-trend--down { color: #16a34a; }

.sdcm-charges { min-height: 140px; }
.sdcm-charges-header { display: flex; justify-content: space-between; margin-bottom: 0.4rem; }
.sdcm-charges-title { font-family: var(--font-body); font-size: 11px; font-weight: 500; text-transform: uppercase; color: #94a3b8; }
.sdcm-charges-note { font-family: var(--font-body); font-size: 10px; color: #94a3b8; }

.sdcm-charge-row { height: 24px; display: flex; align-items: center; gap: 0.4rem; padding: 0 0.3rem; }
.sdcm-charge-row--alt { background: #fafafa; }
.sdcm-anomaly-dot { width: 6px; height: 6px; border-radius: 50%; background: #f0a500; flex-shrink: 0; cursor: help; }
.sdcm-charge-tenant { font-family: var(--font-body); font-size: 12px; color: #1e3a5f; flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sdcm-charge-plot { font-size: 9px; background: #f1f5f9; color: #64748b; border-radius: 4px; padding: 0.02rem 0.3rem; flex-shrink: 0; }
.sdcm-charge-amount { font-family: var(--font-mono); font-size: 12px; color: #1e3a5f; flex-shrink: 0; }

.sdcm-view-all { background: none; border: none; color: #3b82f6; font-family: var(--font-body); font-size: 10px; cursor: pointer; padding: 0.3rem; }

.sdcm-info-note {
  min-height: 48px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 6px; padding: 0.6rem;
  display: flex; gap: 0.5rem; align-items: flex-start;
}
.sdcm-info-icon { color: #3b82f6; font-size: 14px; flex-shrink: 0; margin-top: 0.1rem; }
.sdcm-info-text { font-family: var(--font-body); font-size: 11px; color: #1d4ed8; line-height: 1.5; }

.sdcm-checkbox-row {
  height: 40px; background: #f8f9fc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 0 0.75rem;
  display: flex; align-items: center; gap: 0.5rem; cursor: pointer;
}
.sdcm-checkbox { width: 16px; height: 16px; accent-color: #3b82f6; }
.sdcm-checkbox-row span { font-family: var(--font-body); font-size: 12px; color: #1e3a5f; }

.sdcm-footer {
  height: 56px; flex-shrink: 0; display: flex; align-items: center; gap: 5%; padding: 0 1.25rem; border-top: 1px solid #f1f5f9;
}
.sdcm-footer-cancel {
  flex: 0 0 35%; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 6px; height: 38px;
  font-family: var(--font-body); font-size: 13px; font-weight: 500; color: #1e3a5f; cursor: pointer;
}
.sdcm-footer-confirm {
  flex: 0 0 60%; background: #3b82f6; border: none; border-radius: 6px; height: 38px;
  font-family: var(--font-body); font-size: 13px; font-weight: 500; color: #ffffff; cursor: pointer; transition: background 100ms ease;
}
.sdcm-footer-confirm:hover:not(:disabled) { background: #2563eb; }
.sdcm-footer-confirm:not(:disabled) { box-shadow: 0 0 12px rgba(59, 130, 246, 0.4); }
.sdcm-footer-confirm:disabled { opacity: 0.4; cursor: not-allowed; box-shadow: none; }

.sdcm-state {
  padding: 3rem 1.25rem; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.4rem; min-height: 300px;
}

.sdcm-spinner { font-size: 32px; color: #3b82f6; animation: sdcm-spin 0.8s linear infinite; }
@keyframes sdcm-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.sdcm-state-text { font-family: var(--font-body); font-size: 13px; color: #64748b; margin-top: 0.5rem; }

.sdcm-success-icon { font-size: 40px; color: #10b981; animation: sdcm-pop 300ms ease; }
.sdcm-error-icon { font-size: 40px; color: #dc2626; }
@keyframes sdcm-pop { from { transform: scale(0); } to { transform: scale(1); } }

.sdcm-state-title { font-family: var(--font-display); font-weight: 700; font-size: 18px; margin-top: 0.5rem; }
.sdcm-state-title--success { color: #10b981; }
.sdcm-state-title--error { color: #dc2626; }
.sdcm-state-line { font-family: var(--font-body); font-size: 12px; color: #64748b; }

.sdcm-error-actions { display: flex; gap: 0.5rem; margin-top: 1rem; }
.sdcm-retry-btn { background: #3b82f6; color: #ffffff; border: none; border-radius: 6px; padding: 0.5rem 1rem; font-family: var(--font-body); font-size: 12px; font-weight: 500; cursor: pointer; }
.sdcm-cancel-btn { background: none; border: 1px solid #e2e8f0; color: #64748b; border-radius: 6px; padding: 0.5rem 1rem; font-family: var(--font-body); font-size: 12px; cursor: pointer; }
</style>